import React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { CheckoutForm } from './CheckoutForm';
import { StripeCheckoutProps } from '@usacsmideo/types';
import { getStripePromise, StripeLoadError } from '../../data/payment/stripeLoader';
import { getFeedback } from '@sentry/browser';

export const StripeCheckout = (props: StripeCheckoutProps) => {
  const [stripe, setStripe] = React.useState<Stripe | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  // A TIMEOUT cannot be retried in-page: stripe-js hands back the same hung load
  // promise until the page reloads (see stripeLoader.ts). Only a real error can.
  const [timedOut, setTimedOut] = React.useState(false);
  // "Change or add a discount" can unmount this while Stripe is still loading.
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadStripeInstance = React.useCallback(() => {
    setLoading(true);
    setError(false);
    setTimedOut(false);
    getStripePromise()
      .then((instance) => {
        if (!mounted.current) return;
        setStripe(instance);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!mounted.current) return;
        setError(true);
        setTimedOut(err instanceof StripeLoadError && err.kind === 'timeout');
        setLoading(false);
        // Auto-open Sentry feedback dialog so the patient can report the issue
        const feedback = getFeedback();
        if (feedback) {
          feedback.createForm({ formTitle: 'Payment Issue Report' }).then((form) => {
            form.appendToDom();
            form.open();
          });
        }
      });
  }, []);

  React.useEffect(() => {
    loadStripeInstance();
  }, [loadStripeInstance]);

  if (loading) {
    return <p className="py-4 text-center text-lg text-gray-600">Loading payment form...</p>;
  }

  if (error || !stripe) {
    return (
      <div className="my-4 rounded-md border border-red-300 bg-red-50 p-6 text-center">
        <p className="text-lg font-semibold text-red-800">Unable to connect to our payment provider.</p>
        {timedOut ? (
          <p className="mt-2 text-sm text-red-600">
            The payment form is taking too long to load. Reloading the page usually fixes this.
          </p>
        ) : (
          <p className="mt-2 text-sm text-red-600">
            This may be caused by a slow connection, a content blocker, or a temporary outage. Please try again.
          </p>
        )}
        <button
          type="button"
          onClick={timedOut ? () => window.location.reload() : loadStripeInstance}
          className="mt-4 rounded-md bg-sky-700 px-6 py-2 text-white hover:bg-sky-800"
        >
          {timedOut ? 'Reload Page' : 'Try Again'}
        </button>
        <p className="mt-3 text-xs text-gray-500">
          If this issue persists, please contact{' '}
          <a href="mailto:info@mideohealth.com" className="underline">
            info@mideohealth.com
          </a>
        </p>
      </div>
    );
  }

  const stripeOptions = {
    clientSecret: props.clientSecret,
    appearance: {
      // TODO: Add custom appearance options here if necessary
    },
  };

  return (
    <Elements stripe={stripe} options={stripeOptions}>
      <CheckoutForm
        patientId={props.patientId}
        amountPaid={props.amountPaid}
        couponCode={props.couponCode}
        discountApplied={props.discountApplied}
      />
    </Elements>
  );
};
